// Split VERBATIM from utils/datetime-comprehensive.js (ESM barrel split).
import { DateTime } from 'luxon';
import { BUSINESS_TIMEZONE, nowChicago, utcToChicago } from './dateTimeCore';

export function getCurrentWeekNumber(startDate, currentTime = null, breakPeriods = []) {
  if (!startDate) return 0;
  
  try {
    const start = DateTime.isDateTime(startDate)
      ? startDate.setZone(BUSINESS_TIMEZONE)
      : utcToChicago(startDate);
    
    const now = currentTime
      ? (DateTime.isDateTime(currentTime) ? currentTime.setZone(BUSINESS_TIMEZONE) : utcToChicago(currentTime))
      : nowChicago();
    
    if (!start.isValid || !now.isValid) {
      return 0;
    }
    
    // If current time is before start date, return 0
    if (now < start) {
      return 0;
    }
    
    // Calculate the difference in milliseconds
    let elapsedMs = now.diff(start, 'milliseconds').milliseconds;
    
    // Subtract break periods that have occurred
    // If currently inside a break, freeze progression at the break start boundary
    if (breakPeriods && breakPeriods.length > 0) {
      for (const breakPeriod of breakPeriods) {
        const breakStart = DateTime.isDateTime(breakPeriod.start)
          ? breakPeriod.start.setZone(BUSINESS_TIMEZONE)
          : utcToChicago(breakPeriod.start);
        
        const breakEnd = DateTime.isDateTime(breakPeriod.end)
          ? breakPeriod.end.setZone(BUSINESS_TIMEZONE)
          : utcToChicago(breakPeriod.end);
        
        if (breakStart.isValid && breakEnd.isValid) {
          if (breakEnd <= now) {
            // Break has fully passed - subtract entire break duration
            const breakDuration = breakEnd.diff(breakStart, 'milliseconds').milliseconds;
            elapsedMs -= breakDuration;
          } else if (breakStart <= now && breakEnd > now) {
            // Currently in a break - subtract time from break start to now
            const partialBreak = now.diff(breakStart, 'milliseconds').milliseconds;
            elapsedMs -= partialBreak;
            // Keep timeline just before the break boundary so a new week
            // that starts exactly at break_start does not become available
            // until the break ends.
            elapsedMs = Math.max(0, elapsedMs - 1);
          }
          // Future breaks don't affect current week calculation
        }
      }
    }
    
    // Calculate weeks (7 days = 1 week)
    const diffWeeks = Math.floor(elapsedMs / (7 * 24 * 60 * 60 * 1000));
    
    // Week number is 1-based, so add 1
    return diffWeeks + 1;
  } catch (error) {
    console.error('Error calculating current week number:', error);
    return 0;
  }
}

/**
 * Get the end date/time for a specific week, accounting for break periods
 * @param {string|DateTime} startDate - Start date/time in UTC or DateTime object
 * @param {number} weekNumber - Week number (1-based)
 * @param {Array} [breakPeriods] - Optional array of break periods {start: DateTime, end: DateTime}
 * @returns {DateTime} - End date/time for the week in Chicago timezone
 */

export function getWeekEndDate(startDate, weekNumber, breakPeriods = []) {
  if (!startDate || !weekNumber || weekNumber < 1) return null;
  
  try {
    const start = DateTime.isDateTime(startDate)
      ? startDate.setZone(BUSINESS_TIMEZONE)
      : utcToChicago(startDate);
    
    if (!start.isValid) return null;
    
    // Add calendar weeks in Chicago wall time so DST transitions do not shift
    // assignment deadlines by an hour.
    let endDate = start.setZone(BUSINESS_TIMEZONE, { keepLocalTime: true }).plus({ weeks: weekNumber });
    
    // Add break durations that occur before or during this week
    if (breakPeriods && breakPeriods.length > 0) {
      const sortedBreaks = [...breakPeriods].sort((a, b) => {
        const aStart = DateTime.isDateTime(a.start) ? a.start : utcToChicago(a.start);
        const bStart = DateTime.isDateTime(b.start) ? b.start : utcToChicago(b.start);
        return aStart - bStart;
      });
      
      for (const breakPeriod of sortedBreaks) {
        const breakStart = DateTime.isDateTime(breakPeriod.start)
          ? breakPeriod.start.setZone(BUSINESS_TIMEZONE)
          : utcToChicago(breakPeriod.start);
        
        const breakEnd = DateTime.isDateTime(breakPeriod.end)
          ? breakPeriod.end.setZone(BUSINESS_TIMEZONE)
          : utcToChicago(breakPeriod.end);
        
        if (breakStart.isValid && breakEnd.isValid && breakStart < endDate) {
          // Extend by calendar days in Chicago wall time. Exact elapsed
          // durations reintroduce a one-hour shift when breaks cross DST.
          const breakDays = Math.round(breakEnd.startOf('day').diff(breakStart.startOf('day'), 'days').days);
          endDate = endDate.plus({ days: breakDays });
        }
      }
    }
    
    return endDate;
  } catch (error) {
    console.error('Error calculating week end date:', error);
    return null;
  }
}

/**
 * Check if a week number is available for submission, accounting for break periods
 * @param {string|DateTime} startDate - Start date/time in UTC or DateTime object
 * @param {number} weekNumber - Week number to check
 * @param {DateTime} [currentTime] - Optional current time (defaults to now)
 * @param {Array} [breakPeriods] - Optional array of break periods {start: DateTime, end: DateTime}
 * @returns {Object} - { available: boolean, isLate: boolean, reason: string }
 */

export function isWeekAvailableForSubmission(startDate, weekNumber, currentTime = null, breakPeriods = []) {
  if (!startDate || !weekNumber || weekNumber < 1) {
    return { available: false, isLate: false, reason: 'Invalid week number' };
  }
  
  const currentWeek = getCurrentWeekNumber(startDate, currentTime, breakPeriods);
  
  // Can't submit for future weeks
  if (weekNumber > currentWeek) {
    return {
      available: false,
      isLate: false,
      reason: `Week ${weekNumber} is not available yet. Current week is ${currentWeek || 'not started'}.`
    };
  }
  
  // Can submit for current week or past weeks
  // Past weeks are considered late
  const isLate = weekNumber < currentWeek;
  
  return {
    available: true,
    isLate,
    reason: isLate ? `Week ${weekNumber} submission is late` : `Week ${weekNumber} is current`
  };
}

/**
 * Check if a submission is late based on submission time vs week-specific due date
 * This is different from isWeekAvailableForSubmission which checks if a week is past
 * @param {string|DateTime} startDate - Start date/time in UTC or DateTime object
 * @param {number} weekNumber - Week number (1-based)
 * @param {string|DateTime} submittedAt - Submission time in UTC or DateTime object
 * @param {string|DateTime} [assignmentDueDate] - Optional assignment due_date (for Week 1)
 * @param {Array} [breakPeriods] - Optional array of break periods {start: DateTime, end: DateTime}
 * @returns {boolean} - True if submission is late
 */

export function isSubmissionLate(startDate, weekNumber, submittedAt, assignmentDueDate = null, breakPeriods = []) {
  if (!startDate || !weekNumber || !submittedAt) {
    return false;
  }
  
  try {
    const start = DateTime.isDateTime(startDate)
      ? startDate.setZone(BUSINESS_TIMEZONE)
      : utcToChicago(startDate);
    
    const submitted = DateTime.isDateTime(submittedAt)
      ? submittedAt.setZone(BUSINESS_TIMEZONE)
      : utcToChicago(submittedAt);
    
    if (!start.isValid || !submitted.isValid) {
      return false;
    }
    
    // Calculate week-specific due date
    let weekDueDate;
    if (weekNumber === 1 && assignmentDueDate) {
      // For Week 1, use assignment.due_date if provided
      weekDueDate = DateTime.isDateTime(assignmentDueDate)
        ? assignmentDueDate.setZone(BUSINESS_TIMEZONE)
        : utcToChicago(assignmentDueDate);
    } else {
      // For other weeks, calculate from start_date + week_number weeks, accounting for breaks
      weekDueDate = getWeekEndDate(start, weekNumber, breakPeriods);
    }
    
    if (!weekDueDate || !weekDueDate.isValid) {
      return false;
    }
    
    // Submission is late if submitted AFTER the week's due date
    return submitted > weekDueDate;
  } catch (error) {
    console.error('Error calculating if submission is late:', error);
    return false;
  }
}

/**
 * Get current time in Chicago (alias for nowChicago)
 * @returns {DateTime} - Current time in Chicago
 */
